import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const dashboardItems = [
  { label: '课程覆盖', icon: 'ri-file-list-3-line', desc: '哪些课程已评价' },
  { label: '总分与维度趋势', icon: 'ri-line-chart-line', desc: '质量变化方向' },
  { label: '分档比例', icon: 'ri-pie-chart-line', desc: '质量层次结构' },
  { label: '主题覆盖', icon: 'ri-stack-line', desc: '内容主题分布' },
  { label: '质量热力图', icon: 'ri-grid-line', desc: '课程×维度热力' },
  { label: '资源配置建议', icon: 'ri-settings-3-line', desc: '资源投向方向' },
  { label: '优秀案例分布', icon: 'ri-star-line', desc: '示范经验分布' },
];

export default function P65OutputList({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-6 md:py-8">
        <div ref={titleRef} className="text-center mb-4 md:mb-5">
          <span className={`inline-flex items-center gap-2 text-xs font-body font-semibold text-gold-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />服务管理者
          </span>
          <h2 className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            从单节课记录到<span className="text-gold-600">质量全景</span>
          </h2>
          <p className={`text-sm text-foreground-600 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            管理者需要的不是更多报表，而是从真实课堂证据中看清质量结构
          </p>
        </div>

        {/* Drill-down indicator */}
        <div className={`max-w-3xl mx-auto mb-4 transition-all duration-1000 delay-250 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center justify-center gap-2 px-4 py-2 rounded-full glass border border-gold-200/30 mx-auto w-fit">
            <i className="ri-arrow-down-s-line text-sm text-gold-600" />
            <span className="text-xs font-body font-semibold text-gold-700">总体趋势 → 下钻到学校 → 教师 → 单课证据</span>
            <i className="ri-arrow-down-s-line text-sm text-gold-600" />
          </div>
        </div>

        <div className={`max-w-5xl mx-auto transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
            {dashboardItems.map((item, i) => (
              <div
                key={item.label}
                className="p-4 glass-liquid rounded-xl text-center"
                style={{ transitionDelay: `${300 + i * 80}ms` }}
              >
                <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-primary-100/60 border border-primary-200/40 flex items-center justify-center">
                  <i className={`${item.icon} text-lg text-primary-600`} />
                </div>
                <h4 className="text-sm font-body font-bold text-foreground-800 mb-1">{item.label}</h4>
                <p className="text-xs text-foreground-500 font-body">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className={`max-w-3xl mx-auto text-center mt-4 transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-5 glass-liquid rounded-xl">
            <p className="text-sm text-foreground-600 font-body leading-relaxed">
              管理者关心的不是某一节课的某一个片段，而是一批课程的整体情况。AI可以把单课评价汇总成趋势、比例、地图和专题结果，帮助从<strong className="text-gold-700">"查看一节课"转向"看清一类问题"</strong>。所有汇总结果都保留向下追溯路径。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}